import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Progress,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  useToast,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  List,
  ListItem,
  ListIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import {
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiUpload,
  FiFileText,
  FiUser,
  FiMail,
  FiCalendar,
} from "react-icons/fi";
import { useParams } from "react-router-dom";
import preBoardingService from "../services/preBoardingService";
import DocumentUploadModal from "./DocumentUploadModal";

const CandidatePortal = () => {
  const { offerId } = useParams();
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadModalData, setUploadModalData] = useState(null);

  const toast = useToast();

  useEffect(() => {
    if (offerId) {
      fetchPacket();
    }
  }, [offerId]);

  const fetchPacket = async () => {
    try {
      setLoading(true);
      const response = await preBoardingService.getPacketByOffer(offerId);
      setPacket(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch pre-boarding information",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = (documentIndex) => {
    setUploadModalData({
      documentIndex,
      document: packet.documents[documentIndex],
    });
  };

  const handleUploadSuccess = () => {
    setUploadModalData(null);
    fetchPacket(); // Refresh packet data
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: "yellow",
      uploaded: "blue",
      verified: "green",
      rejected: "red",
    };
    return statusColors[status] || "gray";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: FiClock,
      uploaded: FiUpload,
      verified: FiCheckCircle,
      rejected: FiAlertCircle,
    };
    const IconComponent = icons[status] || FiClock;
    return <IconComponent />;
  };

  const isOverdue = () => {
    return (
      packet &&
      new Date(packet.dueDate) < new Date() &&
      packet.status !== "completed"
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const groupDocumentsByCategory = (documents) => {
    const grouped = {};
    documents.forEach((doc, index) => {
      const category = doc.documentType.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push({ ...doc, originalIndex: index });
    });
    return grouped;
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Text>Loading your pre-boarding information...</Text>
      </Box>
    );
  }

  if (!packet) {
    return (
      <Box p={6}>
        <Alert status="warning">
          <AlertIcon />
          <AlertTitle>Pre-boarding Not Available</AlertTitle>
          <AlertDescription>
            No pre-boarding packet found for this offer. Please contact HR for
            assistance.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  const groupedDocuments = groupDocumentsByCategory(packet.documents);
  const mandatoryDocs = packet.documents.filter(
    (doc) => doc.documentType.isMandatory
  );
  const completedDocs = mandatoryDocs.filter(
    (doc) => doc.status === "verified"
  );
  const rejectedDocs = packet.documents.filter(
    (doc) => doc.status === "rejected"
  );

  return (
    <Box p={6} maxW="1200px" mx="auto">
      <VStack align="stretch" spacing={6}>
        {/* Header */}
        <Box>
          <Heading size="lg" mb={2}>
            Welcome to Pre-boarding
          </Heading>
          <Text color="gray.600">
            Complete your document submission to prepare for your first day
          </Text>
        </Box>

        {/* Status Alert */}
        {isOverdue() && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Action Required!</AlertTitle>
            <AlertDescription>
              Your pre-boarding is overdue. Please complete all required
              documents immediately.
            </AlertDescription>
          </Alert>
        )}

        {rejectedDocs.length > 0 && (
          <Alert status="warning">
            <AlertIcon />
            <AlertTitle>Documents Need Attention</AlertTitle>
            <AlertDescription>
              {rejectedDocs.length} document(s) have been rejected and need to
              be re-uploaded.
            </AlertDescription>
          </Alert>
        )}

        {packet.status === "completed" && (
          <Alert status="success">
            <AlertIcon />
            <AlertTitle>Pre-boarding Complete!</AlertTitle>
            <AlertDescription>
              All your documents have been verified. You're all set for your
              first day!
            </AlertDescription>
          </Alert>
        )}

        {/* Progress Overview */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Overall Progress</StatLabel>
                <StatNumber>
                  {Math.round(packet.overallProgress || 0)}%
                </StatNumber>
                <Progress
                  value={packet.overallProgress || 0}
                  colorScheme="blue"
                  size="sm"
                  mt={2}
                />
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Documents Completed</StatLabel>
                <StatNumber color="green.500">
                  {completedDocs.length} / {mandatoryDocs.length}
                </StatNumber>
                <StatHelpText>Required documents</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Due Date</StatLabel>
                <StatNumber
                  fontSize="md"
                  color={isOverdue() ? "red.500" : "blue.500"}
                >
                  {formatDate(packet.dueDate)}
                </StatNumber>
                <StatHelpText color={isOverdue() ? "red.500" : "inherit"}>
                  {isOverdue() ? "Overdue" : "Complete by this date"}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Status</StatLabel>
                <StatNumber fontSize="sm">
                  <Badge colorScheme={getStatusColor(packet.status)}>
                    {packet.status.replace("_", " ").toUpperCase()}
                  </Badge>
                </StatNumber>
                <StatHelpText>Current status</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Candidate Information */}
        <Card>
          <CardHeader>
            <Heading size="md">Your Information</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <HStack>
                <FiUser />
                <Box>
                  <Text fontWeight="bold">Name</Text>
                  <Text>{packet.candidateName}</Text>
                </Box>
              </HStack>
              <HStack>
                <FiMail />
                <Box>
                  <Text fontWeight="bold">Email</Text>
                  <Text>{packet.candidateEmail}</Text>
                </Box>
              </HStack>
              {packet.metadata?.designation && (
                <HStack>
                  <FiFileText />
                  <Box>
                    <Text fontWeight="bold">Position</Text>
                    <Text>{packet.metadata.designation}</Text>
                  </Box>
                </HStack>
              )}
              {packet.metadata?.joiningDate && (
                <HStack>
                  <FiCalendar />
                  <Box>
                    <Text fontWeight="bold">Start Date</Text>
                    <Text>{formatDate(packet.metadata.joiningDate)}</Text>
                  </Box>
                </HStack>
              )}
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Documents by Category */}
        <Card>
          <CardHeader>
            <Heading size="md">Required Documents</Heading>
            <Text fontSize="sm" color="gray.600">
              Upload all required documents to complete your pre-boarding
            </Text>
          </CardHeader>
          <CardBody>
            <Accordion allowMultiple>
              {Object.entries(groupedDocuments).map(([category, documents]) => (
                <AccordionItem key={category}>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <HStack>
                        <Text fontWeight="medium">{category}</Text>
                        <Badge>
                          {
                            documents.filter((doc) => doc.status === "verified")
                              .length
                          }{" "}
                          /{" "}
                          {
                            documents.filter(
                              (doc) => doc.documentType.isMandatory
                            ).length
                          }
                        </Badge>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <List spacing={3}>
                      {documents.map((doc) => (
                        <ListItem key={doc.originalIndex}>
                          <HStack justify="space-between" align="center">
                            <HStack>
                              <ListIcon
                                as={getStatusIcon(doc.status)}
                                color={`${getStatusColor(doc.status)}.500`}
                              />
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="medium">
                                  {doc.documentType.name}
                                </Text>
                                {doc.documentType.description && (
                                  <Text fontSize="sm" color="gray.600">
                                    {doc.documentType.description}
                                  </Text>
                                )}
                                {doc.documentType.isMandatory && (
                                  <Badge size="xs" colorScheme="red">
                                    Required
                                  </Badge>
                                )}
                                {doc.rejectionReason && (
                                  <Text fontSize="sm" color="red.500" mt={1}>
                                    Rejected: {doc.rejectionReason}
                                  </Text>
                                )}
                              </VStack>
                            </HStack>

                            <VStack align="end" spacing={1}>
                              <Badge colorScheme={getStatusColor(doc.status)}>
                                {doc.status.toUpperCase()}
                              </Badge>
                              {(doc.status === "pending" ||
                                doc.status === "rejected") && (
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  leftIcon={<FiUpload />}
                                  onClick={() =>
                                    handleDocumentUpload(doc.originalIndex)
                                  }
                                  isDisabled={packet.status === "completed"}
                                >
                                  Upload
                                </Button>
                              )}
                              {doc.uploadedAt && (
                                <Text fontSize="xs" color="gray.500">
                                  Uploaded: {formatDate(doc.uploadedAt)}
                                </Text>
                              )}
                              {doc.verifiedAt && (
                                <Text fontSize="xs" color="green.500">
                                  Verified: {formatDate(doc.verifiedAt)}
                                </Text>
                              )}
                            </VStack>
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </CardBody>
        </Card>

        {/* Next Steps */}
        {packet.status !== "completed" && (
          <Card>
            <CardHeader>
              <Heading size="md">Next Steps</Heading>
            </CardHeader>
            <CardBody>
              <List spacing={2}>
                <ListItem>
                  <ListIcon as={FiCheckCircle} color="blue.500" />
                  Upload all required documents using the upload buttons above
                </ListItem>
                <ListItem>
                  <ListIcon as={FiClock} color="yellow.500" />
                  Wait for HR team to verify your documents
                </ListItem>
                <ListItem>
                  <ListIcon as={FiUser} color="green.500" />
                  Complete process before your start date
                </ListItem>
              </List>
            </CardBody>
          </Card>
        )}
      </VStack>

      {/* Document Upload Modal */}
      {uploadModalData && (
        <DocumentUploadModal
          isOpen={!!uploadModalData}
          onClose={() => setUploadModalData(null)}
          packetId={packet._id}
          documentIndex={uploadModalData.documentIndex}
          document={uploadModalData.document}
          onSuccess={handleUploadSuccess}
        />
      )}
    </Box>
  );
};

export default CandidatePortal;
